const defaultParseServerUrl = '/app';
const parseServerUrl = (import.meta.env.VITE_PARSE_SERVER_URL || defaultParseServerUrl).replace(/\/$/, '');
const parseAppId = import.meta.env.VITE_PARSE_APP_ID || 'shbjnnhfcp';
export const buildConfigId = import.meta.env.VITE_AYURGUIDE_BUILD_CONFIG_ID || 'kZF05Q6YgV';
export const tenantId = import.meta.env.VITE_AYURGUIDE_TENANT_ID || 'Ym7CFwMBC5';
const userStorageKey = 'ayurguide-v3-user';
const sessionStorageKey = 'ayurguide-v3-session';

export type ParseRecord = Record<string, any> & { objectId: string; createdAt?: string; updatedAt?: string };
export type ParsePointer = { __type: 'Pointer'; className: string; objectId: string };
export type ParseFileValue = { __type?: 'File'; name?: string; url?: string; _url?: string };

export function pointer(className: string, objectId: string): ParsePointer {
  return { __type: 'Pointer', className, objectId };
}

export function buildConfigPointer() { return pointer('elearning_BuildConfig', buildConfigId); }

export function pointerId(value: unknown): string | undefined {
  if (value && typeof value === 'object') {
    const candidate = value as { id?: string; objectId?: string; __type?: string };
    if (candidate.objectId) return candidate.objectId;
    if (candidate.id) return candidate.id;
  }
  if (typeof value === 'string') return value.split('$').pop();
  return undefined;
}

/** Resolve both legacy URL fields and Parse File values before they reach an image element. */
export function parseFileUrl(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const url = value.trim();
    return url || undefined;
  }
  if (value && typeof value === 'object') {
    const file = value as ParseFileValue;
    if (typeof file.url === 'string' && file.url.trim()) return file.url.trim();
    if (typeof file._url === 'string' && file._url.trim()) return file._url.trim();
  }
  return undefined;
}

export function loadStudentCourseFolderIndex() {
  return request<{ results: Array<ParseRecord & { CourseCount: number }> }>('/student/course-folders');
}

export function loadStudentCourseFolderCatalog(folderId: string) {
  return request<{ folder: ParseRecord; courses: ParseRecord[] }>(`/student/course-folders/${encodeURIComponent(folderId)}`);
}

export function loadStudentCoursePreview(courseId: string) {
  return request<{ course: ParseRecord; contentAllowed: boolean }>(`/student/courses/${encodeURIComponent(courseId)}`);
}

class QueryBuilder {
  where: Record<string, unknown> = {};
  includes: string[] = [];
  equalTo(key: string, value: unknown) { this.where[key] = serialize(value); return this; }
  notEqualTo(key: string, value: unknown) { this.where[key] = { $ne: serialize(value) }; return this; }
  containedIn(key: string, values: unknown[]) { this.where[key] = { $in: values.map(serialize) }; return this; }
  matches(key: string, value: RegExp) { this.where[key] = { $regex: value.source, $options: value.flags }; return this; }
  orWhere(...conditions: Record<string, unknown>[]) { this.where.$or = conditions; return this; }
  exists(key: string, value = true) { this.where[key] = { $exists: value }; return this; }
  include(path: string) { this.includes.push(path); return this; }
}

function serialize(value: unknown): unknown {
  if (value && typeof value === 'object' && '__type' in value) return value;
  return value;
}

async function request<T>(path: string, init: RequestInit = {}) {
  const headers = new Headers(init.headers);
  headers.set('X-Parse-Application-Id', parseAppId);
  headers.set('Accept', 'application/json');
  if (init.body) headers.set('Content-Type', 'application/json');
  // Explicit per-call tokens win over the stored one (e.g. User.become during re-login).
  if (!headers.has('X-Parse-Session-Token')) {
    const sessionToken = sessionStorage.getItem(sessionStorageKey);
    if (sessionToken) headers.set('X-Parse-Session-Token', sessionToken);
  }
  const response = await fetch(`${parseServerUrl}${path}`, { ...init, headers });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Parse request failed (${response.status})`);
  return body as T;
}

export function requestParseApi<T>(path: string, init: RequestInit = {}) {
  return request<T>(path, init);
}

export async function uploadParseFile(file: File, signal?: AbortSignal, onProgress?: (loaded: number, total: number) => void) {
  if (file.size > 12 * 1024 * 1024) throw new Error('Files must be smaller than 12 MB.');
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '-').slice(-120);
  const headers = new Headers({
    'X-Parse-Application-Id': parseAppId,
    'Content-Type': file.type || 'application/octet-stream',
  });
  const sessionToken = sessionStorage.getItem(sessionStorageKey);
  if (sessionToken) headers.set('X-Parse-Session-Token', sessionToken);
  if (!onProgress) {
    const response = await fetch(`${parseServerUrl}/files/${encodeURIComponent(safeName)}`, { method: 'POST', headers, body: file, signal });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || `File upload failed (${response.status})`);
    return body as { __type: 'File'; name: string; url: string };
  }
  return new Promise<{ __type: 'File'; name: string; url: string }>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', `${parseServerUrl}/files/${encodeURIComponent(safeName)}`);
    headers.forEach((value, key) => request.setRequestHeader(key, value));
    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress(event.loaded, event.total);
    });
    request.addEventListener('load', () => {
      let body: { error?: string; __type?: 'File'; name?: string; url?: string } = {};
      try { body = JSON.parse(request.responseText); } catch { /* handled as a failed response below */ }
      if (request.status < 200 || request.status >= 300) {
        reject(new Error(body.error || `File upload failed (${request.status})`));
        return;
      }
      resolve(body as { __type: 'File'; name: string; url: string });
    });
    request.addEventListener('error', () => reject(new Error('File upload failed because the network connection was interrupted.')));
    request.addEventListener('abort', () => reject(new DOMException('The upload was cancelled.', 'AbortError')));
    if (signal) {
      if (signal.aborted) {
        request.abort();
        return;
      }
      signal.addEventListener('abort', () => request.abort(), { once: true });
    }
    request.send(file);
  });
}

export async function queryScoped(className: string, options: { limit?: number; skip?: number; order?: string } = {}) {
  return queryClass(className, { ...options, appScoped: true });
}

export type QueryClassOptions = {
  limit?: number;
  skip?: number;
  order?: string;
  include?: string[];
  keys?: string[];
  where?: (query: QueryBuilder) => void;
  appScoped?: boolean;
};

export async function queryClass(className: string, options: QueryClassOptions = {}) {
  const query = new QueryBuilder();
  if (options.appScoped !== false) query.equalTo('AppId', buildConfigPointer());
  options.include?.forEach((path) => query.include(path));
  options.where?.(query);
  const params = new URLSearchParams({ where: JSON.stringify(query.where), limit: String(options.limit ?? 100) });
  if (options.skip) params.set('skip', String(options.skip));
  if (options.order) params.set('order', options.order);
  if (query.includes.length) params.set('include', query.includes.join(','));
  if (options.keys?.length) params.set('keys', options.keys.join(','));
  const body = await request<{ results: ParseRecord[] }>(`/classes/${encodeURIComponent(className)}?${params.toString()}`);
  return body.results ?? [];
}

/** Load every matching record in bounded requests instead of silently stopping at one page. */
export async function queryAllClass(className: string, options: QueryClassOptions = {}) {
  const pageSize = Math.min(options.limit ?? 100, 100);
  const records: ParseRecord[] = [];
  for (let skip = 0; ; skip += pageSize) {
    const page = await queryClass(className, { ...options, limit: pageSize, skip });
    records.push(...page);
    if (page.length < pageSize) return records;
  }
}

export async function countClass(className: string, options: { where?: (query: QueryBuilder) => void; appScoped?: boolean } = {}) {
  const query = new QueryBuilder();
  if (options.appScoped !== false) query.equalTo('AppId', buildConfigPointer());
  options.where?.(query);
  const params = new URLSearchParams({ where: JSON.stringify(query.where), count: '1', limit: '0' });
  const body = await request<{ count: number }>(`/classes/${encodeURIComponent(className)}?${params.toString()}`);
  return body.count ?? 0;
}

export async function getBuildConfig() {
  const keys = ['AppLogo', 'MobAppName', 'PackageName', 'CheckVersionId', 'AllowFloatingVideo', 'AllowScreenShot', 'PoweredByVisible', 'DefaultDashboardCourseSort', 'DefaultDashboardFreeVidSort', 'DefaultDashboardMiscSort'];
  return request<ParseRecord>(`/classes/elearning_BuildConfig/${buildConfigId}?keys=${keys.join(',')}`);
}

export async function runCloud<T = unknown>(name: string, params: Record<string, unknown> = {}, options: { headers?: HeadersInit } = {}) {
  const body = await request<{ result: T }>(`/functions/${encodeURIComponent(name)}`, { method: 'POST', headers: options.headers, body: JSON.stringify(params) });
  return body.result;
}

export async function saveClassRecord(className: string, data: Record<string, unknown>, objectId?: string) {
  const payload = { ...data };
  if (!objectId && !payload.AppId && className !== 'w_DBFunctions') payload.AppId = buildConfigPointer();
  const body = await request<ParseRecord>(objectId ? `/classes/${encodeURIComponent(className)}/${objectId}` : `/classes/${encodeURIComponent(className)}`, { method: objectId ? 'PUT' : 'POST', body: JSON.stringify(payload) });
  return { ...body, objectId: body.objectId };
}

export async function deleteClassRecord(className: string, objectId: string) {
  await request(`/classes/${encodeURIComponent(className)}/${objectId}`, { method: 'DELETE' });
}

export function currentUser() {
  try { return JSON.parse(sessionStorage.getItem(userStorageKey) || 'null'); } catch { return null; }
}

function storeUser(user: unknown, token?: string) {
  const sessionUser = user && typeof user === 'object'
    ? Object.fromEntries(Object.entries(user).filter(([key]) => key !== 'sessionToken'))
    : user;
  sessionStorage.setItem(userStorageKey, JSON.stringify(sessionUser));
  localStorage.removeItem(userStorageKey);
  if (token) sessionStorage.setItem(sessionStorageKey, token);
}

export const Parse = {
  User: {
    current: currentUser,
    async become(sessionToken: string) {
      const user = await request<ParseRecord>('/users/me', { headers: { 'X-Parse-Session-Token': sessionToken } });
      storeUser(user, sessionToken);
      return user;
    },
    async logIn(username: string, password: string) {
      const body = await request<ParseRecord & { sessionToken: string }>('/login', { method: 'POST', body: JSON.stringify({ username, password }) });
      storeUser(body, body.sessionToken);
      return body;
    },
  async logOut() { await request('/logout', { method: 'POST' }).catch(() => undefined); sessionStorage.removeItem(sessionStorageKey); sessionStorage.removeItem(userStorageKey); sessionStorage.removeItem('ayurguide-v3-admin-role'); localStorage.removeItem(userStorageKey); },
  },
};

export async function logout() { await Parse.User.logOut(); }

/** True when the stored session token is still accepted by the local API. */
export async function hasValidSession(): Promise<boolean> {
  return Boolean(await loadCurrentUserFromSession());
}

/** Resolve identity from the tab's own session token, never from shared localStorage. */
export async function loadCurrentUserFromSession(): Promise<ParseRecord | null> {
  if (!sessionStorage.getItem(sessionStorageKey)) return null;
  try {
    const user = await request<ParseRecord>('/users/me');
    sessionStorage.setItem(userStorageKey, JSON.stringify(user));
    localStorage.removeItem(userStorageKey);
    return user;
  } catch {
    sessionStorage.removeItem(userStorageKey);
    return null;
  }
}

export function clearStoredSession() {
  sessionStorage.removeItem(sessionStorageKey);
  sessionStorage.removeItem(userStorageKey);
  sessionStorage.removeItem('ayurguide-v3-admin-role');
  localStorage.removeItem(userStorageKey);
}
